import type { ConfigPayload } from './template-form.util';

/**
 * The subset of a Traffic Analysis row this mapper reads. Structurally compatible
 * with `TrafficRow` (shared/model) so callers can pass rows directly, while keeping
 * this pure util free of a model dependency.
 */
export interface TrafficFlowInput {
    terminator_remote_address?: string;
    terminator_remote_port?: string | number;
    identity_name?: string;
    host_entity_name?: string;
    service_name?: string;
    protocol?: string;
    circuits?: number;
}

/**
 * Maps observed Traffic Analysis rows into the ingredients for a bespoke
 * (microsegmented) OpenZiti service: an intercept.v1 config, a host.v1 config,
 * and the identities to grant Dial / Bind.
 *
 * Pure, framework-agnostic. Identity *names* are carried through here; resolving
 * them to controller ids and building `@id` policy selectors is the caller's job
 * (it needs the controller), as is fetching the real config-type ids.
 */

// Well-known OpenZiti config-type ids (stable across controllers; also used by
// ziti-console-lib's simple-service wizard). Callers may override after looking
// them up by name against the controller's config-types.
export const INTERCEPT_V1_CONFIG_TYPE_ID = 'g7cIWbcGg';
export const HOST_V1_CONFIG_TYPE_ID = 'NH5p4FpGR';
export const INTERCEPT_V1_CONFIG_TYPE_NAME = 'intercept.v1';
export const HOST_V1_CONFIG_TYPE_NAME = 'host.v1';

export const DEFAULT_PROTOCOLS = ['tcp', 'udp'];

/** Tag key + value stamped on a service and its configs when they are created from the Traffic Analysis
 *  microsegmentation workflow. Traffic Analysis coverage counts only resources carrying this, so a
 *  catch-all or an unrelated service never reads as "covered". */
export const MICROSEGMENT_SOURCE_TAG_KEY = 'nf-microsegment-source';
export const MICROSEGMENT_SOURCE_TRAFFIC = 'traffic-analysis';

/** Tag key whose value is the created service's own Ziti id. Stamped on that service's configs and
 *  dial/bind policies so the whole microsegment (one service + its configs + policies + identities)
 *  can be assembled and managed as a unit, without a separate group id. */
export const MICROSEGMENT_SERVICE_ID_TAG_KEY = 'nf-microsegment-service-id';

export interface PortRange {
    low: number;
    high: number;
}

/** One host delivery dimension: forward what the client used, or pin a fixed value. */
export interface HostDimension {
    forward: boolean;
    /** The fixed value when not forwarding (a protocol, address, or port as a string). */
    value?: string;
}

/**
 * Explicit per-dimension host.v1 delivery, built from the per-service hosting controls. Each of
 * protocol, address and port is either forwarded (deliver using what the client dialed) or pinned to
 * a fixed value. A forwarded dimension may be narrowed by its allowed set; when that set is left empty
 * the intercept's own value is used. Optional health checks are written onto the host config.
 */
export interface HostSpec {
    protocol: HostDimension;
    address: HostDimension;
    port: HostDimension;
    allowedProtocols?: string[];
    allowedAddresses?: string[];
    allowedPortRanges?: PortRange[];
    /** Source IPs/CIDRs the host accepts connections from (host.v1 `allowedSourceAddresses`). */
    allowedSourceAddresses?: string[];
}

/** The editable ingredients extracted from a traffic selection. */
export interface MicrosegmentDraft {
    serviceName: string;
    addresses: string[];
    portRanges: PortRange[];
    protocols: string[];
    dialIdentityNames: string[];
    bindIdentityNames: string[];
    /** Existing services the observed traffic flowed through (context / tagging). */
    sourceServiceNames: string[];
    /** Summed circuit count across the selection. */
    circuits: number;
    /**
     * Where traffic is delivered. When omitted, the host forwards to whatever address the client
     * reached (a single address/port collapses to that one concrete host). When set, every
     * intercepted address is instead delivered to this one fixed host the operator named.
     */
    fixedHost?: { address: string; port?: number };
    /**
     * Explicit per-dimension host delivery. When set it fully describes the host.v1 config and takes
     * precedence over fixedHost and the single-vs-forwarding default below.
     */
    host?: HostSpec;
}

export interface MicrosegmentConfigTypeIds {
    interceptConfigTypeId?: string;
    hostConfigTypeId?: string;
}

const nonEmpty = (v: string | undefined | null): v is string => !!v && `${v}`.trim().length > 0;

const distinct = (values: (string | undefined | null)[]): string[] => {
    const seen = new Set<string>();
    const out: string[] = [];
    values.forEach((v) => {
        if (nonEmpty(v) && !seen.has(v)) {
            seen.add(v);
            out.push(v);
        }
    });
    return out;
};

/** Merge a list of ports into sorted, coalesced ranges (consecutive ports collapse). */
export function collapsePortRanges(ports: number[]): PortRange[] {
    const unique = Array.from(new Set(ports.filter((p) => Number.isInteger(p) && p >= 0))).sort((a, b) => a - b);
    const ranges: PortRange[] = [];
    unique.forEach((port) => {
        const last = ranges[ranges.length - 1];
        if (last && port === last.high + 1) {
            last.high = port;
        } else {
            ranges.push({ low: port, high: port });
        }
    });
    return ranges;
}

/** Replace anything outside [a-zA-Z0-9-] with '-' and squeeze/trim separators. */
function sanitizeNamePart(value: string): string {
    return value
        .trim()
        .replace(/[^a-zA-Z0-9-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

/** A readable, controller-safe default service name derived from the destinations. */
export function suggestServiceName(addresses: string[], portRanges: PortRange[]): string {
    const base = 'microseg';
    if (addresses.length === 0) {
        return base;
    }
    const firstAddr = sanitizeNamePart(addresses[0]) || 'segment';
    if (addresses.length > 1) {
        return `${base}-${firstAddr}-and-${addresses.length - 1}-more`;
    }
    if (portRanges.length === 1 && portRanges[0].low === portRanges[0].high) {
        return `${base}-${firstAddr}-${portRanges[0].low}`;
    }
    return `${base}-${firstAddr}`;
}

/** Extract the editable microsegment ingredients from one or more traffic rows. */
export function buildMicrosegmentDraft(rows: TrafficFlowInput[]): MicrosegmentDraft {
    const safeRows = (rows ?? []).filter((r) => !!r);

    const addresses = distinct(safeRows.map((r) => r.terminator_remote_address));
    const ports = safeRows
        .map((r) => parseInt(`${r.terminator_remote_port}`, 10))
        .filter((p) => !isNaN(p));
    const portRanges = collapsePortRanges(ports);

    const protocolsPresent = distinct(safeRows.map((r) => r.protocol));
    const protocols = protocolsPresent.length > 0 ? protocolsPresent : [...DEFAULT_PROTOCOLS];

    const dialIdentityNames = distinct(safeRows.map((r) => r.identity_name));
    const bindIdentityNames = distinct(safeRows.map((r) => r.host_entity_name));
    const sourceServiceNames = distinct(safeRows.map((r) => r.service_name));
    const circuits = safeRows.reduce((sum, r) => sum + (Number(r.circuits) || 0), 0);

    return {
        serviceName: suggestServiceName(addresses, portRanges),
        addresses,
        portRanges,
        protocols,
        dialIdentityNames,
        bindIdentityNames,
        sourceServiceNames,
        circuits,
    };
}

/** Build host.v1 data from an explicit per-dimension HostSpec. A forwarded dimension carries its
 *  allowed set (defaulting to the intercept's own values); a fixed dimension carries a concrete value.
 *  Optional port/http checks are appended. */
function buildHostDataFromSpec(draft: MicrosegmentDraft): Record<string, any> {
    const host = draft.host as HostSpec;
    const data: Record<string, any> = {};

    if (host.protocol.forward) {
        data.forwardProtocol = true;
        data.allowedProtocols = host.allowedProtocols?.length ? [...host.allowedProtocols] : [...draft.protocols];
    } else if (host.protocol.value) {
        data.protocol = host.protocol.value;
    }

    if (host.address.forward) {
        data.forwardAddress = true;
        data.allowedAddresses = host.allowedAddresses?.length ? [...host.allowedAddresses] : [...draft.addresses];
    } else if (host.address.value) {
        data.address = host.address.value;
    }

    if (host.port.forward) {
        data.forwardPort = true;
        data.allowedPortRanges = host.allowedPortRanges?.length
            ? host.allowedPortRanges.map((r) => ({ low: r.low, high: r.high }))
            : draft.portRanges.map((r) => ({ low: r.low, high: r.high }));
    } else if (host.port.value) {
        data.port = Number(host.port.value);
    }

    if (host.allowedSourceAddresses?.length) {
        data.allowedSourceAddresses = [...host.allowedSourceAddresses];
    }

    return data;
}

/**
 * Build the intercept.v1 + host.v1 config payloads for a (possibly edited) draft.
 * When `host` is set it fully describes the host.v1 config (per-dimension forward/fixed).
 * Otherwise, when `fixedHost` is set, every intercepted address is delivered to that one host;
 * a single address + single port yields a concrete host.v1 (address/port); and anything broader
 * uses host.v1 forwarding (forwardAddress/forwardPort) with the allowed sets, so one consolidated
 * service can cover a whole group.
 */
export function buildMicrosegmentConfigPayloads(
    draft: MicrosegmentDraft,
    ids: MicrosegmentConfigTypeIds = {}
): ConfigPayload[] {
    const serviceName = draft.serviceName;
    const interceptConfigTypeId = ids.interceptConfigTypeId || INTERCEPT_V1_CONFIG_TYPE_ID;
    const hostConfigTypeId = ids.hostConfigTypeId || HOST_V1_CONFIG_TYPE_ID;

    const interceptPayload: ConfigPayload = {
        configTypeId: interceptConfigTypeId,
        name: `${serviceName}-intercept-config`,
        data: {
            addresses: [...draft.addresses],
            portRanges: draft.portRanges.map((r) => ({ low: r.low, high: r.high })),
            protocols: [...draft.protocols],
        },
    };

    const singleAddress = draft.addresses.length === 1;
    const singlePort = draft.portRanges.length === 1 && draft.portRanges[0].low === draft.portRanges[0].high;

    let hostData: Record<string, any>;
    if (draft.host) {
        hostData = buildHostDataFromSpec(draft);
    } else if (draft.fixedHost) {
        hostData = {
            address: draft.fixedHost.address,
            port: draft.fixedHost.port,
            forwardProtocol: true,
            allowedProtocols: [...draft.protocols],
        };
    } else if (singleAddress && singlePort) {
        hostData = {
            address: draft.addresses[0],
            port: draft.portRanges[0].low,
            forwardProtocol: true,
            allowedProtocols: [...draft.protocols],
        };
    } else {
        hostData = {
            forwardProtocol: true,
            allowedProtocols: [...draft.protocols],
            forwardAddress: true,
            allowedAddresses: [...draft.addresses],
            forwardPort: true,
            allowedPortRanges: draft.portRanges.map((r) => ({ low: r.low, high: r.high })),
        };
    }

    const hostPayload: ConfigPayload = {
        configTypeId: hostConfigTypeId,
        name: `${serviceName}-host-config`,
        data: hostData,
    };

    return [interceptPayload, hostPayload];
}
